/**
 * Codec filtering utilities for HLS video streams
 * Prioritizes codecs in order: AV1 > HEVC > VP9 > x264 (H.264)
 */

/**
 * @typedef {Object} HlsLevel
 * @property {number} id
 * @property {number} width
 * @property {number} height
 * @property {number} bitrate
 * @property {string} videoCodec
 * @property {string} [audioCodec]
 * @property {string} codecSet
 * @property {string[]} url
 */

/**
 * @typedef {Object} CodecSupportChecker
 * @property {function(string): boolean} isTypeSupported
 */

/**
 * Generate codec string for MediaSource support testing
 * @param {HlsLevel} level
 * @returns {string|null}
 */
export function getCodecString(level) {
  if (!level.videoCodec) return null;

  const audioCodec = level.audioCodec;
  let codecString = `video/mp4; codecs="${level.videoCodec}`;

  if (audioCodec) {
    codecString += `, ${audioCodec}"`;
  } else {
    codecString += '"';
  }

  return codecString;
}

/**
 * Check if codec is supported using MediaSource or a custom checker
 * @param {HlsLevel} level
 * @param {CodecSupportChecker} [checker=globalThis.MediaSource]
 * @returns {boolean}
 */
export function isCodecSupported(level, checker = globalThis.MediaSource) {
  if (!checker) return false;

  const codecString = getCodecString(level);
  if (!codecString) return false;

  try {
    return checker.isTypeSupported(codecString);
  } catch (_e) {
    return false;
  }
}

/**
 * Get the best codec type from available levels based on priority and support
 * @param {HlsLevel[]} levels
 * @param {CodecSupportChecker} [checker=globalThis.MediaSource]
 * @returns {string|null}
 */
export function getBestCodecType(levels, checker = globalThis.MediaSource) {
  if (!Array.isArray(levels)) return null;

  // Check for codec support in priority order
  const hasAV1 = levels.some((level) => {
    const codecSet = level.codecSet || '';
    return codecSet.includes('av01') && isCodecSupported(level, checker);
  });

  const hasHEVC = levels.some((level) => {
    const codecSet = level.codecSet || '';
    return codecSet.includes('hvc1') && isCodecSupported(level, checker);
  });

  const hasVP9 = levels.some((level) => {
    const codecSet = level.codecSet || '';
    return codecSet.includes('vp09') && isCodecSupported(level, checker);
  });

  if (hasAV1) {
    return 'av01';
  } else if (hasHEVC) {
    return 'hvc1';
  } else if (hasVP9) {
    return 'vp09';
  } else {
    return null; // Keep all levels (fallback to x264/H.264)
  }
}

/**
 * Get indices of levels to remove based on best codec type
 * @param {HlsLevel[]} levels
 * @param {string} bestCodecType
 * @param {CodecSupportChecker} [checker=globalThis.MediaSource]
 * @returns {number[]}
 */
export function getLevelsToRemove(
  levels,
  bestCodecType,
  checker = globalThis.MediaSource,
) {
  if (!bestCodecType || !Array.isArray(levels)) return [];

  const levelsToRemove = [];

  for (let i = levels.length - 1; i >= 0; i--) {
    const level = levels[i];
    const codecSet = level.codecSet || '';
    const isSupported = isCodecSupported(level, checker);
    const matchesCodec = codecSet.includes(bestCodecType);

    // Remove if it doesn't match the best codec type or isn't supported
    if (!matchesCodec || !isSupported) {
      levelsToRemove.push(i);
    }
  }

  return levelsToRemove;
}

/**
 * Filter levels by keeping only the best codec type
 * @param {HlsLevel[]} levels
 * @param {CodecSupportChecker} [checker=globalThis.MediaSource]
 * @returns {{filteredLevels: HlsLevel[], removedIndices: number[], codecType: string|null}}
 */
export function filterLevelsByCodec(levels, checker = globalThis.MediaSource) {
  const bestCodecType = getBestCodecType(levels, checker);

  if (!bestCodecType) {
    return {
      filteredLevels: levels,
      removedIndices: [],
      codecType: null,
    };
  }

  const removedIndices = getLevelsToRemove(levels, bestCodecType, checker);
  const filteredLevels = levels.filter((_, index) =>
    !removedIndices.includes(index)
  );

  return {
    filteredLevels,
    removedIndices,
    codecType: bestCodecType,
  };
}

/**
 * Get human-readable codec name for logging
 * @param {string|null} codecType
 * @returns {string}
 */
export function getCodecDisplayName(codecType) {
  switch (codecType) {
    case 'av01':
      return 'AV1';
    case 'hvc1':
      return 'HEVC';
    case 'vp09':
      return 'VP9';
    default:
      return 'x264';
  }
}
